import { useClientUserSendInvitationToAccountEmail } from 'api/requests';
import {
  LegalClientEntity,
  LegalClientRepresentativeEntity,
  OnboardingStatusType,
  PermissionType,
} from 'api/types/entity';
import { useTranslation } from 'libs/i18n';
import { ROUTES, useNavigate } from 'libs/navigation';
import { useNotify } from 'libs/notify';
import { usePermissions } from 'libs/permissions';
import { useRevokeOtpVerification } from 'modules/client/common/hooks';

interface Args {
  legalClient: LegalClientEntity | null;
  legalClientRepresentative: LegalClientRepresentativeEntity | null;
  refetch: () => Promise<any>;
}

export const useLegalClientRepresentativeActions = ({
  legalClient,
  legalClientRepresentative,
  refetch,
}: Args) => {
  const notify = useNotify();
  const { t } = useTranslation();
  const { navigate } = useNavigate();
  const { checkPermissions } = usePermissions();

  const { revokeOtpVerification, revokeOtpVerificationLoading } =
    useRevokeOtpVerification();

  const { sendInvitationToAccountEmail } =
    useClientUserSendInvitationToAccountEmail();

  const onChangeClientType = () => {
    if (legalClientRepresentative?.legalClientId)
      navigate(
        ROUTES.clients.legalClients.legalClient.fullPath,
        {
          legalClientId: legalClientRepresentative?.legalClientId,
        },
        { replace: true }
      );
  };

  const onShowVersionHistory =
    legalClient &&
    legalClientRepresentative?.versionNumber &&
    legalClientRepresentative?.versionNumber > 1
      ? () => {
          navigate(
            ROUTES.clients.legalClients.legalClient.legalClientVersion
              .legalClientRepresentativeVersion.fullPath,
            {
              legalClientId: legalClient.id,
              versionId: legalClientRepresentative.versionId!,
              legalClientRepresentativeId: legalClient.representativeId!,
            }
          );
        }
      : undefined;

  const onRevokeOtpVerification = legalClientRepresentative?.user?.otpVerified
    ? async () =>
        revokeOtpVerification(legalClientRepresentative?.user?.id!, refetch)
    : undefined;

  const onSendInvitationToAccount =
    checkPermissions([PermissionType.Auth_SendInvitationToAccountEmail]) &&
    legalClient?.onboardingStatus === OnboardingStatusType.AccountOpened &&
    legalClientRepresentative?.user?.id &&
    !legalClientRepresentative?.user?.isActive
      ? async () => {
          const res = await sendInvitationToAccountEmail(
            legalClientRepresentative!.user!.id!
          );

          if (res.resendTime) {
            notify.info(t('common.invitationHasBeenSent'));
          }

          return res.resendTime;
        }
      : undefined;

  return {
    revokeOtpVerificationLoading,
    onChangeClientType,
    onShowVersionHistory,
    onRevokeOtpVerification,
    onSendInvitationToAccount,
  };
};
